import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, Download, Save, FileDown, Plus, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, errMsg, downloadFile } from './taUtils';
import { previewPlan } from './templatePlan';
import { FlightGantt } from './FlightGantt';
import { TemplateActivitiesTable } from './TemplateActivitiesTable';
import { TurnaroundSettingsCard } from './TurnaroundSettingsCard';

export function TemplateEditor() {
    const { token } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [data, setData] = useState(null);
    const [selId, setSelId] = useState(null);
    const [rows, setRows] = useState([]);
    const [standard, setStandard] = useState(30);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);
    const [newType, setNewType] = useState('');
    const fileRef = useRef(null);

    const load = useCallback(async (keepId) => {
        try {
            const res = await axios.get(`${API}/turnaround/templates`, { headers });
            setData(res.data);
            const t = res.data.templates.find(x => x.id === keepId) || res.data.templates[0];
            setSelId(t.id);
            setRows(t.activities.map(({ base_start_min, base_end_min, critical, link_label, ...a }) => a));
            setStandard(t.standard_minutes);
        } catch (e) { toast.error(errMsg(e, 'Error al cargar la plantilla')); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
    const tpl = data.templates.find(t => t.id === selId);
    const editable = data.can_edit;
    const preview = previewPlan(rows);
    const handle = async (fn, ok) => {
        setSaving(true); setErrors([]);
        try { const r = await fn(); toast.success(ok); await load(r?.data?.id || selId); }
        catch (e) { const d = e?.response?.data?.detail; setErrors(d?.errors || [errMsg(e)]); toast.error(errMsg(e)); }
        finally { setSaving(false); }
    };
    const save = () => handle(() => axios.put(`${API}/turnaround/templates/${selId}`, { activities: rows, standard_minutes: Number(standard) }, { headers }), 'Plantilla guardada');
    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const text = await f.text();
        handle(() => axios.post(`${API}/turnaround/templates/${selId}/import-csv`, { csv: text }, { headers }), `CSV importado: ${f.name}`);
    };
    const createType = () => handle(() => axios.post(`${API}/turnaround/templates`, { aircraft_type: newType }, { headers }).then(r => { setNewType(''); return r; }), 'Plantilla por tipo de aeronave creada');

    return (
        <div className="space-y-4" data-testid="template-editor">
            <Card className="card-aviation"><CardContent className="p-4 space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1.5">
                        <Label>Plantilla</Label>
                        <Select value={selId} onValueChange={(v) => load(v)}>
                            <SelectTrigger className="w-[240px]" data-testid="tpl-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{data.templates.map(t => <SelectItem key={t.id} value={t.id}>{t.aircraft_type ? `${t.name} (${t.aircraft_type})` : t.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Tiempo estándar de rotación (min)</Label>
                        <Input type="number" min="1" className="w-[120px] font-mono" value={standard} disabled={!editable} onChange={e => setStandard(e.target.value)} data-testid="tpl-standard" />
                    </div>
                    <div className="text-xs text-muted-foreground pb-2" data-testid="tpl-planned-total">Planificado calculado: <span className="font-mono font-bold text-foreground">{preview ? `${preview.total} min` : '—'}</span></div>
                    <div className="flex flex-wrap gap-2 ml-auto">
                        {editable && <Button onClick={save} disabled={saving || !preview} data-testid="tpl-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar</Button>}
                        {editable && <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={saving} data-testid="tpl-import"><Upload className="w-4 h-4 mr-2" />Importar CSV</Button>}
                        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="tpl-import-input" />
                        <Button variant="outline" onClick={() => downloadFile(`${API}/turnaround/templates/${selId}/export-csv`, headers, `turnaround_${tpl?.aircraft_type || 'general'}.csv`)} data-testid="tpl-export"><Download className="w-4 h-4 mr-2" />Exportar CSV</Button>
                        <Button variant="ghost" onClick={() => downloadFile(`${API}/turnaround/templates/default-csv`, headers, 'AeroOps_Plantilla_Turnaround.csv')} data-testid="tpl-default-csv"><FileDown className="w-4 h-4 mr-2" />Plantilla por defecto</Button>
                    </div>
                </div>
                {editable && (
                    <p className="text-[11px] text-muted-foreground">Importar CSV reemplaza las actividades; el tiempo estándar no viene en el CSV y se mantiene el valor actual.</p>
                )}
                {editable && (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Plantilla distinta por tipo de aeronave:</span>
                        <Input value={newType} onChange={e => setNewType(e.target.value)} placeholder="Ej.: Saab 340" className="h-8 w-44 text-xs" data-testid="tpl-new-type" />
                        <Button size="sm" variant="outline" disabled={!newType.trim() || saving} onClick={createType} data-testid="tpl-create-type"><Plus className="w-3.5 h-3.5 mr-1" />Crear</Button>
                    </div>
                )}
                {(errors.length > 0 || !preview) && (
                    <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-700 dark:text-red-300 space-y-1" data-testid="tpl-errors">
                        <p className="flex items-center gap-1 font-semibold"><AlertTriangle className="w-3.5 h-3.5" />Revisa la plantilla</p>
                        {!preview && <p>Hay predecesores inexistentes o un ciclo entre actividades.</p>}
                        {errors.map((er, i) => <p key={i}>{er}</p>)}
                    </div>
                )}
                {preview && <FlightGantt activities={preview.activities} standard={Number(standard)} planOnly showPre={data.settings?.show_pre_arrival !== false} />}
            </CardContent></Card>
            <TemplateActivitiesTable rows={rows} setRows={setRows} links={data.links} editable={editable} />
            <TurnaroundSettingsCard settings={data.settings} editable={editable} headers={headers} onSaved={() => load(selId)} />
        </div>
    );
}
